import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'tc_onboarding_v1';

const STEP_ORDER = [
  'prompt',
  'settings-intro',
  'settings-schedule',
  'settings-bookings',
  'settings-online',
  'settings-publicpage',
  'settings-reminders',
  'settings-access',
  'shifts-intro',
  'shifts-assign',
  'shifts-new',
  'done',
];

const OnboardingContext = createContext(null);

export const OnboardingProvider = ({ children }) => {
  const { user, clinic } = useAuth();
  const [step, setStep] = useState(null);
  // Track the previous userId so we can detect a fresh login (null → userId)
  const prevUserIdRef = useRef(null);

  useEffect(() => {
    if (!user) {
      // User logged out — reset so the next login is detected as "new"
      prevUserIdRef.current = null;
      setStep(null);
      return;
    }

    // Wait until clinic data is loaded (needed for the owner check)
    if (!clinic) return;

    // Only show the tour to the clinic owner (admin), not to vet employees
    if (clinic.user_id !== user.id) {
      setStep(null);
      return;
    }

    const isNewLogin = prevUserIdRef.current !== user.id;
    prevUserIdRef.current = user.id;

    const stored = localStorage.getItem(STORAGE_KEY);
    let data = null;
    if (stored) {
      try { data = JSON.parse(stored); } catch { /* ignore */ }
    }

    if (!data || data.userId !== user.id) {
      // First ever visit for this user
      setStep('prompt');
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ step: 'prompt', userId: user.id }));
      return;
    }

    let currentStep = data.step;

    // 'deferred' = user said "Later" → show prompt again on the next login
    if (currentStep === 'deferred' && isNewLogin) {
      currentStep = 'prompt';
      // Keep localStorage as 'deferred' so subsequent logins also show the prompt
    }

    setStep(currentStep);
  }, [user?.id, clinic?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const persistStep = useCallback((newStep) => {
    setStep(newStep);
    if (user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ step: newStep, userId: user.id }));
    }
  }, [user]);

  // User said "Yes" — start the tour
  const startTour = useCallback(() => persistStep('settings-intro'), [persistStep]);

  // User said "No" — never show again
  const skipTour = useCallback(() => persistStep('skipped'), [persistStep]);

  // User said "Later" — show again next login, hide for current session
  const deferTour = useCallback(() => {
    if (user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ step: 'deferred', userId: user.id }));
    }
    setStep('deferred'); // hides the bubble for the rest of this session
  }, [user]);

  // Advance to next step
  const nextStep = useCallback(() => {
    const idx = STEP_ORDER.indexOf(step);
    if (idx >= 0 && idx < STEP_ORDER.length - 1) {
      persistStep(STEP_ORDER[idx + 1]);
    }
  }, [step, persistStep]);

  // Mark tour complete
  const completeTour = useCallback(() => persistStep('completed'), [persistStep]);

  // Jump directly to a specific step
  const goToStep = useCallback((targetStep) => persistStep(targetStep), [persistStep]);

  return (
    <OnboardingContext.Provider value={{ step, startTour, skipTour, deferTour, nextStep, completeTour, goToStep }}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider');
  return ctx;
};
